/**
 * External stroke-order reference links, keyed by {@link Letter} id.
 *
 * On-screen tracing was descoped (see the `refactor(hindi)!` commit that
 * removed `src/content/strokes/`). Rather than re-drawing stroke paths in
 * app, we link out to verified third-party stroke-order media so a learner
 * can still see how a letter is built.
 *
 * Only entries that were independently verified to resolve (HTTP 200 or a
 * successful Commons `imageinfo` lookup) are included here. Coverage is
 * partial by design — vowels and base consonants only; there is no known
 * stroke-order source for matras, signs, numerals, nukta letters, or
 * conjuncts (see the research notes that produced this data).
 */

/** A single external stroke-order reference for one letter. */
export interface WritingGuide {
  /** Direct URL to the stroke-order image/animation. Always `https:`. */
  url: string;
  /** Short attribution of where the guide comes from, e.g. `"wikimedia"`. */
  source: string;
}

/** Writing guides indexed by {@link Letter} id. Verified entries only. */
export const writingGuideById: ReadonlyMap<string, WritingGuide> = new Map([
  [
    "vowel-a",
    {
      url: "https://upload.wikimedia.org/wikipedia/commons/c/c9/Devanagari_a_%E0%A4%85.gif",
      source: "wikimedia",
    },
  ],
  [
    "vowel-aa",
    {
      url: "https://upload.wikimedia.org/wikipedia/commons/e/e0/Devanagari_aa_%E0%A4%86.gif",
      source: "wikimedia",
    },
  ],
  [
    "vowel-i",
    {
      url: "https://upload.wikimedia.org/wikipedia/commons/3/32/Devanagari_i_%E0%A4%87.gif",
      source: "wikimedia",
    },
  ],
  [
    "vowel-ii",
    {
      url: "https://upload.wikimedia.org/wikipedia/commons/d/d9/Devanagari_ii_%E0%A4%88.gif",
      source: "wikimedia",
    },
  ],
  [
    "vowel-u",
    {
      url: "https://upload.wikimedia.org/wikipedia/commons/2/22/Devanagari_u_%E0%A4%89.gif",
      source: "wikimedia",
    },
  ],
  [
    "vowel-uu",
    {
      url: "https://upload.wikimedia.org/wikipedia/commons/4/4c/Devanagari_uu_%E0%A4%8A.gif",
      source: "wikimedia",
    },
  ],
  [
    "vowel-e",
    {
      url: "https://upload.wikimedia.org/wikipedia/commons/3/37/Devanagari_e_%E0%A4%8F.gif",
      source: "wikimedia",
    },
  ],
  [
    "vowel-ai",
    {
      url: "https://upload.wikimedia.org/wikipedia/commons/c/cf/Devanagari_%C9%9B_%E0%A4%90.gif",
      source: "wikimedia",
    },
  ],
  [
    "vowel-o",
    {
      url: "https://upload.wikimedia.org/wikipedia/commons/0/0b/Devanagari_o_%E0%A4%93.gif",
      source: "wikimedia",
    },
  ],
  [
    "vowel-au",
    {
      url: "https://upload.wikimedia.org/wikipedia/commons/4/42/Devanagari_au_%E0%A4%94.gif",
      source: "wikimedia",
    },
  ],
  [
    "vowel-ri",
    {
      url: "https://upload.wikimedia.org/wikipedia/commons/d/da/Devanagari_r_%E0%A4%8B.gif",
      source: "wikimedia",
    },
  ],
  [
    "ka",
    {
      url: "https://upload.wikimedia.org/wikipedia/commons/f/ff/Devanagari_k_%E0%A4%95.gif",
      source: "wikimedia",
    },
  ],
  [
    "kha",
    {
      url: "https://upload.wikimedia.org/wikipedia/commons/c/c3/Devanagari_kh_%E0%A4%96.gif",
      source: "wikimedia",
    },
  ],
  [
    "ga",
    {
      url: "https://upload.wikimedia.org/wikipedia/commons/0/0d/Devanagari_g_%E0%A4%97.gif",
      source: "wikimedia",
    },
  ],
  [
    "gha",
    {
      url: "https://upload.wikimedia.org/wikipedia/commons/6/64/Devanagari_gh_%E0%A4%98.gif",
      source: "wikimedia",
    },
  ],
  [
    "nga",
    {
      url: "https://upload.wikimedia.org/wikipedia/commons/0/00/Deva-%E0%A4%99-order.gif",
      source: "wikimedia",
    },
  ],
  [
    "ca",
    {
      url: "https://upload.wikimedia.org/wikipedia/commons/2/29/Devanagari_tch_%E0%A4%9A.gif",
      source: "wikimedia",
    },
  ],
  [
    "cha",
    {
      url: "https://upload.wikimedia.org/wikipedia/commons/0/0d/Devanagari_tchh_%E0%A4%9B.gif",
      source: "wikimedia",
    },
  ],
  [
    "ja",
    {
      url: "https://upload.wikimedia.org/wikipedia/commons/6/65/Devanagari_d%CA%92_%E0%A4%9C.gif",
      source: "wikimedia",
    },
  ],
  [
    "jha",
    {
      url: "https://upload.wikimedia.org/wikipedia/commons/d/d4/Devanagari_d%CA%92%CA%B0_%E0%A4%9D.gif",
      source: "wikimedia",
    },
  ],
  [
    "nya",
    {
      url: "https://upload.wikimedia.org/wikipedia/commons/7/76/Devanagari_%C9%B2_%E0%A4%9E.gif",
      source: "wikimedia",
    },
  ],
  [
    "tta",
    {
      url: "https://upload.wikimedia.org/wikipedia/commons/5/5d/Devanagari_t%CC%A5_%E0%A4%9F.gif",
      source: "wikimedia",
    },
  ],
  [
    "ttha",
    {
      url: "https://upload.wikimedia.org/wikipedia/commons/7/70/Devanagari_%CA%88%CA%B0_%E0%A4%A0.gif",
      source: "wikimedia",
    },
  ],
  [
    "dda",
    {
      url: "https://upload.wikimedia.org/wikipedia/commons/e/ed/Devanagari_%C9%96_%E0%A4%A1.gif",
      source: "wikimedia",
    },
  ],
  [
    "ddha",
    {
      url: "https://upload.wikimedia.org/wikipedia/commons/4/40/Devanagari_%C9%96%CA%B1_%E0%A4%A2.gif",
      source: "wikimedia",
    },
  ],
  [
    "nna",
    {
      url: "https://upload.wikimedia.org/wikipedia/commons/9/91/Devanagari_%C9%B3_%E0%A4%A3.gif",
      source: "wikimedia",
    },
  ],
  [
    "ta",
    {
      url: "https://upload.wikimedia.org/wikipedia/commons/7/7a/Devanagari_t_%E0%A4%A4.gif",
      source: "wikimedia",
    },
  ],
  [
    "tha",
    {
      url: "https://upload.wikimedia.org/wikipedia/commons/d/d2/Devanagari_t%CA%B0_%E0%A4%A5.gif",
      source: "wikimedia",
    },
  ],
  [
    "da",
    {
      url: "https://upload.wikimedia.org/wikipedia/commons/4/49/Devanagari_d_%E0%A4%A6.gif",
      source: "wikimedia",
    },
  ],
  [
    "dha",
    {
      url: "https://upload.wikimedia.org/wikipedia/commons/9/99/Devanagari_d%CA%B0_%E0%A4%A7.gif",
      source: "wikimedia",
    },
  ],
  [
    "na",
    {
      url: "https://upload.wikimedia.org/wikipedia/commons/0/08/Devanagari_n_%E0%A4%A8.gif",
      source: "wikimedia",
    },
  ],
  [
    "pa",
    {
      url: "https://upload.wikimedia.org/wikipedia/commons/1/1c/Devanagari_p_%E0%A4%AA.gif",
      source: "wikimedia",
    },
  ],
  [
    "pha",
    {
      url: "https://upload.wikimedia.org/wikipedia/commons/5/51/Devanagari_p%CA%B0_%E0%A4%AB.gif",
      source: "wikimedia",
    },
  ],
  [
    "ba",
    {
      url: "https://upload.wikimedia.org/wikipedia/commons/4/46/Devanagari_b_%E0%A4%AC.gif",
      source: "wikimedia",
    },
  ],
  [
    "bha",
    {
      url: "https://upload.wikimedia.org/wikipedia/commons/f/f3/Devanagari_b%CA%B0_%E0%A4%AD.gif",
      source: "wikimedia",
    },
  ],
  [
    "ma",
    {
      url: "https://upload.wikimedia.org/wikipedia/commons/4/41/Devanagari_m_%E0%A4%AE.gif",
      source: "wikimedia",
    },
  ],
  [
    "ya",
    {
      url: "https://upload.wikimedia.org/wikipedia/commons/e/eb/Devanagari_j_%E0%A4%AF.gif",
      source: "wikimedia",
    },
  ],
  [
    "ra",
    {
      url: "https://upload.wikimedia.org/wikipedia/commons/9/98/Devanagari_r_%E0%A4%B0.gif",
      source: "wikimedia",
    },
  ],
  [
    "la",
    {
      url: "https://upload.wikimedia.org/wikipedia/commons/7/70/Devanagari_l_%E0%A4%B2.gif",
      source: "wikimedia",
    },
  ],
  [
    "va",
    {
      url: "https://upload.wikimedia.org/wikipedia/commons/5/57/Devanagari_v_%E0%A4%B5.gif",
      source: "wikimedia",
    },
  ],
  [
    "sha",
    {
      url: "https://upload.wikimedia.org/wikipedia/commons/7/73/Devanagari_%C3%A7_%E0%A4%B6.gif",
      source: "wikimedia",
    },
  ],
  [
    "ssa",
    {
      url: "https://upload.wikimedia.org/wikipedia/commons/d/df/Devanagari_%CA%82_%E0%A4%B7.gif",
      source: "wikimedia",
    },
  ],
  [
    "sa",
    {
      url: "https://upload.wikimedia.org/wikipedia/commons/d/df/Devanagari_s_%E0%A4%B8.gif",
      source: "wikimedia",
    },
  ],
  [
    "ha",
    {
      url: "https://upload.wikimedia.org/wikipedia/commons/a/a8/Devanagari_42_h%2C_%C9%A6_%E0%A4%B9.gif",
      source: "wikimedia",
    },
  ],
]);

/** General Devanagari stroke-order conventions, summarized from source material. */
export interface StrokeOrderRules {
  /** Plain-English summary of the general stroke-order conventions. */
  summaryEn: string;
  /** Source URLs the summary was drawn from. */
  sources: readonly string[];
}

export const strokeOrderRules: StrokeOrderRules = {
  summaryEn:
    "Devanagari letters are generally written left to right and top to bottom, like most of the strokes that make up a character. Most letters begin with the left-side vertical or the top-left curve first, then add interior strokes, then the right-side vertical/hook last. The horizontal head-stroke (shirorekha, the bar that letters hang from and that joins into a continuous line across a word) is drawn LAST, after the body of the letter/word is complete, not first — this is the single most distinctive rule versus Latin-script habits. Diacritics that sit below the baseline (e.g. the u/uu hooks, r-matra) are added after the consonant body; diacritics above the head-stroke (e.g. e/ai signs, anusvara dot, chandrabindu) are added last of all, after the shirorekha itself. Circular/looping strokes (as in ई, ऊ, घ) are drawn in a single continuous motion rather than built from straight segments. Because Devanagari is not taught with a codified national stroke-order standard (unlike CJK scripts), the conventions above are descriptive best practice drawn from font-design and pedagogical sources, and minor variation between teachers/sources is normal and expected.",
  sources: [
    "https://commons.wikimedia.org/wiki/Category:Devanagari_stroke_order",
    "https://en.wiktionary.org/wiki/File:Devanagari_%E0%A4%9D_stroke_order.svg",
    "https://www.type-together.com/devanagari-type-anatomy",
    "https://www.omniglot.com/writing/devanagari.htm",
  ],
};
